import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, Loader2, X } from 'lucide-react';
import { billingAPI } from '../services/api';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useBilling } from '../contexts/BillingContext';
import { useToast } from '../contexts/ToastContext';
import { PLAN_DISPLAY } from '../types/billing';
import { parseServerIso } from '../utils/datetime';

// Renders inline inside a sentence, so an unparseable date collapses to ''
// rather than the NO_DATE placeholder used in table cells.
const formatDate = (iso?: string | null) => {
  const d = parseServerIso(iso);
  if (!d) return '';
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const PendingPlanBanner: React.FC = () => {
  const { activeWorkspace } = useWorkspace();
  const { billing, refetch } = useBilling();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);
  const [undoing, setUndoing] = useState(false);

  const pending = billing?.pending_plan_change;
  if (!pending || dismissed || !activeWorkspace) return null;

  const targetName = PLAN_DISPLAY[pending.target_plan]?.name ?? pending.target_plan;
  const targetInterval = pending.target_billing_interval === 'year' ? 'Yearly' : pending.target_billing_interval === 'month' ? 'Monthly' : null;

  const handleUndo = async () => {
    setUndoing(true);
    try {
      await billingAPI.cancelDowngrade(activeWorkspace.id);
      showToast('Plan change cancelled. Your current plan stays active.', 'success');
      await refetch();
    } catch (err: any) {
      showToast(err?.response?.data?.detail || 'Failed to cancel plan change', 'error');
    } finally {
      setUndoing(false);
    }
  };

  return (
    <div className="bg-gradient-to-r from-amber-500 to-orange-600 text-white px-4 py-3 shadow-md">
      <div className="max-w-7xl mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-start sm:items-center space-x-3 min-w-0">
          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5 sm:mt-0" />
          <p className="text-sm font-medium leading-snug">
            Your plan will change to{' '}
            <span className="font-bold">
              {targetName}
              {targetInterval ? ` · ${targetInterval}` : ''}
            </span>{' '}
            on <span className="font-bold">{formatDate(pending.effective_at)}</span>.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => navigate('/billing')}
            className="bg-white/15 hover:bg-white/25 text-white px-3 py-1.5 rounded-lg text-xs sm:text-sm font-semibold transition-colors"
          >
            View details
          </button>
          <button
            onClick={handleUndo}
            disabled={undoing}
            className="bg-white text-orange-600 px-3 py-1.5 rounded-lg text-xs sm:text-sm font-semibold hover:bg-gray-100 transition-colors disabled:opacity-50 flex items-center gap-1.5"
          >
            {undoing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
            Undo
          </button>
          <button
            onClick={() => setDismissed(true)}
            className="text-white/70 hover:text-white p-1 rounded transition-colors"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PendingPlanBanner;
